import { useState, useMemo, useEffect } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import CircularProgress from '@mui/material/CircularProgress'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import debounce from 'lodash/debounce'
import { useHistory } from 'react-router-dom'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { TableCellTooltip } from '@components'
import { colors } from '@global/constants'
import { useQueryString } from '@global/hooks'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { getCellContent } from 'functions'
import { CvsTable } from '../../../components/table/cvs-table'
import { EnhancedTableToolbar } from '../../../components/table/enhanced-table-toolbar'
import { t } from '@lingui/core/macro'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    height: '100%',
  },
  paper: {
    width: '100%',
    overflow: 'hidden',
    maxHeight: '100%',
    height: '100%',
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  tableWrapper: {
    // overflowX: 'auto',
  },
  textField: {
    width: 200,
  },
}))

export const AdminSearch = () => {
  const history = useHistory()
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const { setUrl, setStateFromQuery } = useQueryString(searchTerm, setSearchTerm)
  const [DATASETS_ENABLED] = useAppSelector(selectFeatureIncludes([FeatureDict.DATASETS_PAGE]))

  useEffect(() => {
    if (history.location.search) {
      setStateFromQuery(term => {
        setLoading(true)
        setSearchTerm(term)
        search(term)
      })
    }
  }, [])

  const search = term => {
    api.post(`/api/core/v1/search/template-data/legacy`, { term }).then(({ data }) => {
      setResults(data.results.map((result, index) => ({ ...result, index })))
      setLoading(false)
    })
  }

  const debouncedSearch = useMemo(() => {
    return debounce(term => {
      search(term)
    }, 650)
  }, [])

  const onSearch = search => {
    setSearchTerm(search)

    if (!search) {
      setLoading(false)
      setUrl()
      setResults([])
    } else {
      setLoading(true)
      setUrl({ q: search })
      debouncedSearch(search)
    }
  }

  const columns = useMemo(
    () => [
      { label: 'Template Name', type: 'text', width: '15%', lookup: 'template_name' },
      { label: 'Submission Name', type: 'text', width: '20%', lookup: 'identifier' },
      { label: 'Submitted', type: 'date', width: '10%', lookup: 'created_at' },
      { label: 'Submitted By', type: 'text', width: '15%', lookup: 'submitted_by' },
      { label: 'Field', type: 'date', width: '20%', lookup: 'field_name' },
      {
        label: 'Value',
        width: '20%',
        lookup: 'cell_value',
      },
    ],
    [],
  )

  return (
    <motion.div
      className={classes.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
    >
      <div style={{ height: 'calc(100% - 60px)', paddingTop: 20 }}>
        <EnhancedTableToolbar
          term={searchTerm}
          filterChanged={onSearch}
          noAdd={true}
          blockMultipleEdit={false}
        />
        {!loading && results.length ? (
          <CvsTable
            columns={columns}
            rows={results}
            orderBy="created_at"
            sortOrder="desc"
            keyField="index"
            noAdd={true}
            noPagination={true}
            style={{ maxWidth: '100%' }}
          >
            {({ row }) => {
              return (
                <TableRow
                  hover
                  onClick={() => history.push(`/codes/details/${row.dataset_id}/data`)}
                  tabIndex={-1}
                  key={row.dataset_id}
                  style={{ cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {row.template_name}
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {row.identifier}
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {getCellContent('date', row.created_at)}
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {row.submitted_by}
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {row.field_name}
                  </TableCell>
                  <TableCellTooltip title={row.cell_value}>
                    <TableCell
                      component="th"
                      scope="row"
                      padding="normal"
                      style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {row.cell_value}
                    </TableCell>
                  </TableCellTooltip>
                </TableRow>
              )
            }}
          </CvsTable>
        ) : (
          <div style={{ padding: 24, display: 'flex', width: '100%', justifyContent: 'center' }}>
            {loading ? (
              <CircularProgress color="primary" />
            ) : (
              <div>
                {searchTerm ? (
                  <Typography variant="h6">{t`No matching results`}</Typography>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <div>
                      <SearchIcon style={{ fontSize: '72px', color: colors.blue.smokey }} />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <Typography variant="h6" gutterBottom style={{ display: 'inline-block' }}>
                        {t`Search successfully submitted values`}
                      </Typography>
                    </div>
                    <Typography variant="body2">
                      {`Quickly track down which submission and ${
                        DATASETS_ENABLED ? 'dataset' : 'template'
                      } contain the desired data`}
                    </Typography>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  )
}
